import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { mockDocuments } from '../../data/mockData';

const KYC_TYPES = new Set(['Aadhaar Card', 'PAN Card', 'Passport', 'Voter ID', 'Driving License']);

const chip = (text) => (
  <Badge variant="outline" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
    {text}
  </Badge>
);

export default function AgentDocuments() {
  const location = useLocation();

  const mode = (() => {
    if (location.pathname.includes('/agent/documents/rejected')) return 'rejected';
    if (location.pathname.includes('/agent/documents/history')) return 'history';
    if (location.pathname.includes('/agent/documents/kyc')) return 'kyc';
    if (location.pathname.includes('/agent/documents/upload')) return 'upload';
    return 'upload';
  })();

  const docs = useMemo(() => {
    if (mode === 'rejected') return mockDocuments.filter((d) => d.status === 'Rejected');
    if (mode === 'kyc') return mockDocuments.filter((d) => KYC_TYPES.has(d.docType) && d.status !== 'Verified');
    if (mode === 'history') return mockDocuments.slice().sort((a, b) => (b.uploadDate || '').localeCompare(a.uploadDate || ''));
    return [];
  }, [mode]);

  return (
    <div className="space-y-6" data-testid="agent-documents">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Documents</h1>
          <p className="text-muted-foreground mt-1">Upload and track document verification status</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant={mode === 'upload' ? 'default' : 'outline'}>
            <Link to="/agent/documents/upload">Upload</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'kyc' ? 'default' : 'outline'}>
            <Link to="/agent/documents/kyc">Pending KYC</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'rejected' ? 'default' : 'outline'}>
            <Link to="/agent/documents/rejected">Rejected</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'history' ? 'default' : 'outline'}>
            <Link to="/agent/documents/history">History</Link>
          </Button>
        </div>
      </div>

      {mode === 'upload' ? (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Upload Documents</CardTitle>
            <CardDescription>Attach documents for a lead (mock upload)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lead ID</Label>
                <Input placeholder="e.g. L001" />
              </div>
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aadhaar">Aadhaar Card</SelectItem>
                    <SelectItem value="pan">PAN Card</SelectItem>
                    <SelectItem value="salary">Salary Slip</SelectItem>
                    <SelectItem value="bank">Bank Statement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>File</Label>
                <Input type="file" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => toast.success('Document uploaded (mock).')}>
                Upload
              </Button>
              <Button variant="outline" onClick={() => toast.message('No server connected in mock mode.')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>
              {mode === 'kyc' ? 'Pending KYC' : mode === 'rejected' ? 'Rejected Documents' : 'Document History'}
            </CardTitle>
            <CardDescription>Documents by lead and status</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead>Verified By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{d.leadName}</span>
                        <span className="text-xs text-muted-foreground font-mono">{d.leadId}</span>
                      </div>
                    </TableCell>
                    <TableCell>{d.docType}</TableCell>
                    <TableCell>{chip(d.status)}</TableCell>
                    <TableCell>{d.uploadDate}</TableCell>
                    <TableCell>{d.verifiedBy ? chip(d.verifiedBy) : <span className="text-muted-foreground">—</span>}</TableCell>
                  </TableRow>
                ))}
                {docs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                      No documents in this view.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
